import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { ProductDocType } from '@lancar-pos/db-schema';

export type Product = ProductDocType;

export function useProductStore() {
  const db = useDatabase();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.products
      .find({
        selector: { _deleted: false },
        sort: [{ name: 'asc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => Product }>) => {
        setProducts(docs.map((doc: { toMutableJSON: () => Product }) => doc.toMutableJSON() as Product));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.products]);

  const addProduct = useCallback(
    async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      await db.products.insert({
        ...product,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.products]
  );

  const updateProduct = useCallback(
    async (id: string, updates: Partial<Omit<Product, 'id' | 'createdAt'>>) => {
      const doc = await db.products.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.products]
  );

  const deleteProduct = useCallback(
    async (id: string) => {
      const doc = await db.products.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.products]
  );

  const getProductById = useCallback(
    (id: string): Product | undefined => {
      return products.find((p) => p.id === id);
    },
    [products]
  );

  const getProductsByCategory = useCallback(
    (categoryId: string | null): Product[] => {
      return products.filter((p) => p.categoryId === categoryId);
    },
    [products]
  );

  const getActiveProducts = useCallback((): Product[] => {
    return products.filter((p) => p.isActive);
  }, [products]);

  const searchProducts = useCallback(
    (query: string): Product[] => {
      const lowerQuery = query.toLowerCase();
      return products.filter(
        (p) =>
          p.isActive &&
          (p.name.toLowerCase().includes(lowerQuery) ||
            p.sku.toLowerCase().includes(lowerQuery) ||
            p.barcode.toLowerCase().includes(lowerQuery))
      );
    },
    [products]
  );

  return {
    products,
    isLoading,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    getProductsByCategory,
    getActiveProducts,
    searchProducts,
  };
}

export type ProductStore = ReturnType<typeof useProductStore>;
